import { createRef, useEffect, useState } from 'react';

import { QATest } from '@src/application/modules/tag/shared/tag/types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSegmentTrack } from '@application/hooks/useSegment/useSegment';

import styles from '@application/modules/shared/styles/CampaignDetails/campaigndetails.module.scss';
import {
  DetailsSection,
  DetailsTable,
  TableCellCondition,
  TableCellStatus,
  TableCellValue,
} from '@application/modules/shared/components/Campaigns/DetailsTable/DetailsTable';
import { useWebsiteData } from '@src/application/modules/tag/shared/tag/context/TagContext/hooks';
import { useCampaignConditions } from '../../hooks/useCampaignConditions';
import {
  getUrlOperatorLabel,
  getSelectorLabel,
  useFavoriteUrlOperatorLabel,
  getDisplayFrequencyTypeText,
} from '../../helpers';
import { TargetingConditions } from '../../types';
import { TestStatusRejected, TestStatusAccepted, FrequencyType } from '../../../shared/tag/serializedTagTypes';
import { AudienceDetails } from '../AudienceDetails.tsx/AudienceDetails';

type CampaignDetailsProps = {
  test: QATest;
  expanded: boolean;
};

const getTargetingConditions = (test: QATest): TargetingConditions => {
  const {
    url_scope: url,
    code_scope: code,
    selector_scope: selector,
    favorite_url_scope: favoriteUrl,
  } = test.targetings.targetPages;

  const { displayFrequencyType, displayFrequencyUnit, displayFrequencyValue } = test.testDatas;
  const { trigger, mapTriggerNameToPosition, segment, mapSegmentNameToPosition } = useCampaignConditions(test);
  return {
    pageUrl: { url, code, selector, favoriteUrl },
    trigger,
    mapTriggerNameToPosition,
    segment,
    mapSegmentNameToPosition,
    displayFrequency: {
      displayFrequencyType,
      displayFrequencyUnit,
      displayFrequencyValue,
    },
  };
};

export function CampaignDetails({ test, expanded }: CampaignDetailsProps) {
  const details = createRef<HTMLDivElement>();
  const [maxHeight, setMaxHeight] = useState<string>();
  const segmentTrack = useSegmentTrack();
  const { t } = useTranslation();
  const { urlHistory } = useWebsiteData();

  useEffect(() => {
    if (details.current) setMaxHeight(`${details.current?.scrollHeight}px`);
  }, [details.current, expanded]);

  const {
    pageUrl: { url, code, selector, favoriteUrl },
    trigger,
    mapTriggerNameToPosition,
    segment,
    mapSegmentNameToPosition,
    displayFrequency,
  } = getTargetingConditions(test);

  const { segmentMode, triggerMode } = test;

  const hasFavoriteUrls = typeof favoriteUrl !== 'undefined';
  const getInclusionString = (include: boolean) => (include ? t('is') : t('is not'));

  const getFavoriteUrlOperatorLabel = useFavoriteUrlOperatorLabel();

  return (
    <div
      className={classNames(styles.detailsContainer, { [styles.expanded]: expanded })}
      style={expanded ? { maxHeight } : {}}
      ref={details}
    >
      <div className={styles['flex-5']} />
      <div className={styles['flex-95']}>
        <div>
          <DetailsTable>
            {hasFavoriteUrls ? (
              <DetailsSection title={t('Page URLs')} array={favoriteUrl?.conditions.favoriteUrlScopeConditions}>
                {({ include, operator, url }) => (
                  <>
                    <TableCellCondition>{getFavoriteUrlOperatorLabel(include, operator)}</TableCellCondition>
                    <TableCellCondition />
                    <TableCellCondition>{url}</TableCellCondition>
                    <TableCellStatus success={favoriteUrl.success} />
                    <TableCellValue success={favoriteUrl.success} value={urlHistory?.current} />
                  </>
                )}
              </DetailsSection>
            ) : (
              <DetailsSection title={t('Page URLs')} array={url?.conditions}>
                {({ include, condition, value }) => (
                  <>
                    <TableCellCondition>{getUrlOperatorLabel(condition, include, t)}</TableCellCondition>
                    <TableCellCondition />
                    <TableCellCondition>{value}</TableCellCondition>
                    <TableCellStatus success={!!url?.success} />
                    <TableCellValue success={!!url?.success} value={urlHistory?.current} />
                  </>
                )}
              </DetailsSection>
            )}

            <DetailsSection title={t('- CSS Selector')} array={selector?.conditions}>
              {({ include, condition, value }) => (
                <>
                  <TableCellCondition>{getSelectorLabel(condition, t)}</TableCellCondition>
                  <TableCellCondition>{getInclusionString(include)}</TableCellCondition>
                  <TableCellCondition>{value}</TableCellCondition>
                  <TableCellStatus success={!!selector?.success} />
                </>
              )}
            </DetailsSection>

            <DetailsSection title={t('- Page URLs by Code')} array={code?.conditions}>
              {({ value }) => (
                <>
                  <TableCellCondition colSpan={3}>{value}</TableCellCondition>
                  <TableCellStatus success={!!code?.success} />
                </>
              )}
            </DetailsSection>

            {segment && Object.keys(segment).length > 1 && (
              <DetailsSection title={t('Segment Mode')} array={[{ segmentMode }]}>
                {({ segmentMode }) => (
                  <TableCellCondition colSpan={3}>{segmentMode === 'some' ? t('Some') : t('All')}</TableCellCondition>
                )}
              </DetailsSection>
            )}

            {segment && Object.keys(segment).length ? (
              <AudienceDetails
                audienceType="segment"
                audiences={segment}
                mapAudienceNameToPosition={mapSegmentNameToPosition}
              />
            ) : (
              <DetailsSection title={t('Segment')} array={[]} />
            )}

            {trigger && Object.keys(trigger).length > 1 && (
              <DetailsSection title={t('Trigger Mode')} array={[{ triggerMode }]}>
                {({ triggerMode }) => (
                  <TableCellCondition colSpan={3}>{triggerMode === 'some' ? t('Some') : t('All')}</TableCellCondition>
                )}
              </DetailsSection>
            )}

            {trigger && Object.keys(trigger).length ? (
              <AudienceDetails
                audienceType="trigger"
                audiences={trigger}
                mapAudienceNameToPosition={mapTriggerNameToPosition}
              />
            ) : (
              <DetailsSection title={t('Trigger')} array={[]} />
            )}

            <DetailsSection title={t('Display Frequency')} array={[displayFrequency]}>
              {({ displayFrequencyType, displayFrequencyUnit, displayFrequencyValue }) => (
                <>
                  <TableCellCondition>{getDisplayFrequencyTypeText(displayFrequencyType, t)}</TableCellCondition>
                  <TableCellCondition>
                    {FrequencyType.regular === displayFrequencyType
                      ? t('every {{displayFrequencyValue}}', { displayFrequencyValue })
                      : ''}
                  </TableCellCondition>
                  <TableCellCondition>
                    {FrequencyType.regular === displayFrequencyType ? displayFrequencyUnit : ''}
                  </TableCellCondition>
                  {(test.status === TestStatusRejected.displayFrequency ||
                    test.status === TestStatusAccepted.accepted) && (
                    <TableCellStatus success={test.status !== TestStatusRejected.displayFrequency} />
                  )}
                </>
              )}
            </DetailsSection>
          </DetailsTable>
        </div>
        <a
          href={`https://app2.abtasty.com/edit/test/${test.testDatas.id}/audience`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
          onClick={() => segmentTrack('Qa - Click edit targeting')}
        >
          {t('Edit Targeting criteria here')}
          <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 12 12" version="1.1">
            <title>Icon/External-Link</title>
            <desc>Created with Sketch.</desc>
            <g id="Icon/External-Link" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <path
                d="M11.8359312,5.76860721 C11.9752364,5.82915176 11.9856591,5.85348948 11.9952432,5.99460025 C11.9952432,7.12444556 12.0060254,8.25441076 11.9951234,9.38437595 C11.9739066,10.7330209 10.8064651,11.9756829 9.37602343,11.9889907 C7.12338417,11.9958244 4.87061313,12.0099715 2.61804576,11.9888708 C1.27248343,11.9681298 0.0377254869,10.8101103 0.0153465699,9.38425606 C-0.0051155233,7.12456545 -0.0051155233,4.86463505 0.0153465699,2.60482455 C0.0365873728,1.25150389 1.20886884,0.0133977306 2.63439866,8.9917655e-05 C2.63439866,8.9917655e-05 6.00521703,8.9917655e-05 6.00521703,8.9917655e-05 C6.00521703,8.9917655e-05 6.16234866,0.0221497157 6.21796051,0.129571341 C6.29270465,0.27391915 6.17783899,0.467781614 6.00521703,0.479650744 C4.87813667,0.479650744 3.75100838,0.469220296 2.62397593,0.479770634 C1.52096368,0.496795044 0.50508305,1.44284866 0.494396758,2.62424676 C0.487604014,4.87554506 0.474126347,7.12708314 0.49451656,9.37826155 C0.511911735,10.4865266 1.46682539,11.4985199 2.63736974,11.5094299 C4.88706188,11.5162636 7.13686184,11.5304107 9.38645814,11.50931 C10.4902251,11.4922856 11.50474,10.5362811 11.5160493,9.36483385 C11.5160493,9.36483385 11.5160852,5.99460025 11.5160852,5.99460025 C11.5160852,5.99460025 11.5799155,5.69679298 11.8359312,5.76860721 Z M11.7244919,8.9917655e-05 C11.72926,-9.76996262e-15 11.7343396,-9.76996262e-15 11.7397426,8.9917655e-05 L11.7556882,8.9917655e-05 L11.7692737,0.000569478482 L11.7727839,0.000689368688 L11.7734548,0.000809258895 L11.774485,0.000809258895 C11.8962034,0.0151960837 11.9904512,0.116862979 11.9952432,0.239870331 L11.9952432,4.07635694 C11.9855393,4.32357055 11.5297426,4.42439821 11.5160852,4.07635694 L11.5160852,0.818700249 L6.17464029,6.164125 C5.96877342,6.35139351 5.85637567,6.21088218 5.80063204,6.11940596 C5.74485247,6.02780984 5.76276279,5.90540194 5.83579378,5.82495561 L11.1773106,0.479530854 L7.9032439,0.478931403 C7.72624919,0.457950617 7.73258669,0.408675742 7.70067158,0.331586339 C7.64074687,0.186758969 7.7526774,0.00668387902 7.92204076,8.9917655e-05 L11.7244919,8.9917655e-05 Z"
                id="Fill-7"
                fill="#000000"
              />
            </g>
          </svg>
        </a>
      </div>
    </div>
  );
}
